import { Button, toast, Typography } from "@suraasa/placebo-ui"
import clsx from "clsx"
import { Page, Xmark } from "iconoir-react"
import { useCallback, useEffect } from "react"
import * as xlsx from "xlsx"

import { UploadedTeacherData } from "../../manage-teacher-sheet/components/add-multiple-teacher-dialog/constants"

type Props = {
  onChange: (data: UploadedTeacherData[]) => void
  onError: (props: {
    title: string
    description: React.ReactNode | string
  }) => void
  resetError: () => void
  files: string | FileList | null
  setFiles: (f: string | FileList | null) => void
}

const inputId = "csv-input-field"
const DragNDropCSV = ({
  onChange,
  onError,
  resetError,
  files,
  setFiles,
}: Props) => {
  const readUploadFile = useCallback(
    (file: FileList | null) => {
      if (!file || file.length === 0) return

      const reader = new FileReader()
      reader.onload = (el: ProgressEvent<FileReader>) => {
        const data = el.target?.result
        if (!data) return
        try {
          resetError()
          const workbook = xlsx.read(data, { type: "array" })
          const worksheet = workbook.Sheets[workbook.SheetNames[0]]
          const json = xlsx.utils.sheet_to_json(worksheet)

          const missingFields: string[] = []

          const formatted = json.map((x: any): UploadedTeacherData => {
            const name = x["name"]
            const email = x["email"]
            const phoneNumber = String(x["phone_number"] ?? "").trim()
            // const subjectsTaught = x["subjects_taught"]
            // const gradesTaught = x["grades_taught"]
            // const boards = x["curricula"]
            // const teachingLevels = x["teaching_levels"]
            // const designation = x["designation"]
            const rowId = x["__rowNum__"]

            if (!name && !missingFields.includes("Name"))
              missingFields.push("Name")
            if (!email && !missingFields.includes("Email"))
              missingFields.push("Email")
            // if (!subjectsTaught && !missingFields.includes("Subjects Taught"))
            //   missingFields.push("Subjects Taught")
            // if (!gradesTaught && !missingFields.includes("Grades Taught"))
            //   missingFields.push("Grades Taught")
            // if (!boards && !missingFields.includes("Boards"))
            //   missingFields.push("Boards")
            // if (!teachingLevels && !missingFields.includes("Teaching Levels"))
            //   missingFields.push("Teaching Levels")

            return {
              name,
              email,
              // subjectsTaught,
              // gradesTaught,
              // boards,
              // teachingLevels,
              // ...(designation ? { designation } : {}),
              ...(phoneNumber
                ? {
                    phoneNumber: phoneNumber.startsWith("+")
                      ? phoneNumber
                      : `+${phoneNumber}`,
                  }
                : {}),
              rowId,
            }
          })

          if (missingFields.length > 0) {
            setFiles(null)

            onError({
              title: "Missing Data",
              description: `The file is missing the following field${
                missingFields.length > 1 ? "s" : ""
              }: ${missingFields.join(", ")}.`,
            })
            toast.error(
              `Missing Data: The file is missing the following field${
                missingFields.length > 1 ? "s" : ""
              }: ${missingFields.join(", ")}.`,
            )
            return
          }

          if (!formatted.length) {
            setFiles(null)
            onError({
              title: "Empty File",
              description: "The file doesn’t appear to have any records.",
            })
            toast.error(
              "Empty File: The file doesn’t appear to have any records.",
            )
            return
          }

          // if (noOfSearAvailable > formatted.length) {
          //   onChange(formatted)
          // } else {
          //   setFiles(null)
          //   const diff = noOfSearAvailable - formatted.length
          //   onError({
          //     title: "Seat Limit Reached ",
          //     description: (
          //       <Typography className="text-onSurface-700" variant="smallBody">
          //         You need{" "}
          //         <strong>
          //           {pluralize("additional seat", Math.abs(diff))}
          //         </strong>{" "}
          //         to invite everyone.{" "}
          //         <a href="" className="text-primary-500">
          //           Contact
          //         </a>{" "}
          //         us to add more seats.
          //       </Typography>
          //     ),
          //   })
          //   toast.error("Seat Limit Reached:")
          //   return
          // }
          setFiles(file)
          onChange(formatted)
        } catch (err: any) {
          setFiles(null)
          toast.error(err.message ?? "Invalid CSV format")
          onError({
            title: "Couldn’t Process CSV",
            description: "The file doesn’t appear to be a valid CSV file.",
          })
        }
      }
      reader.readAsArrayBuffer(file[0])
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [onChange, onError, resetError],
  )

  useEffect(() => {
    const root = document.querySelector("#__fileUploadRoot")
    const box = document.querySelector("#__fileUploadRoot .box__input")

    if (!root) return
    if (!box) return

    const allDragEvents = [
      "drag",
      "dragstart",
      "dragend",
      "dragover",
      "dragenter",
      "dragleave",
      "drop",
    ]

    function preventDefault(e: Event) {
      e.preventDefault()
      e.stopPropagation()
    }
    allDragEvents.forEach((event) => {
      root.addEventListener(event, preventDefault, false)
    })

    const dragOverEvents = ["dragover", "dragenter"]
    function handleDragOver() {
      box?.classList.add("is-dragover")
    }
    dragOverEvents.forEach((event) => {
      box.addEventListener(event, handleDragOver, false)
    })

    const dragLeaveEvents = ["dragleave", "dragend", "drop"]

    function handleDragLeave() {
      box?.classList.remove("is-dragover")
    }
    dragLeaveEvents.forEach((event) => {
      box.addEventListener(event, handleDragLeave, false)
    })

    function handleFileDrop(e: Event) {
      if ("dataTransfer" in e) {
        const droppedFiles = (e as DragEvent).dataTransfer?.files
        readUploadFile(droppedFiles ?? null)
      }
    }
    root.addEventListener("drop", handleFileDrop, false)

    return () => {
      allDragEvents.forEach((event) => {
        root.removeEventListener(event, preventDefault)
      })
      dragOverEvents.forEach((event) => {
        box.removeEventListener(event, handleDragOver)
      })
      dragLeaveEvents.forEach((event) => {
        box.removeEventListener(event, handleDragLeave)
      })
      root.removeEventListener("drop", handleFileDrop)
    }
    /**
     * Files is added to the dependency because when files are added,
     * the "box__input" element unmounts, clearing the event listeners.
     * And the listeners don't re-apply if user removes the file and tries to upload again..
     */
  }, [readUploadFile, files])

  const onClear = () => {
    setFiles(null)
    resetError()
  }

  useEffect(() => {
    return () => {
      resetError()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])
  return (
    <div>
      <div
        className={clsx(
          "h-full [&_.is-dragover]:border-primary-400 [&_.is-dragover]:bg-primary-50 [&_label]:cursor-pointer",
        )}
        id="__fileUploadRoot"
      >
        {files ? (
          <div className="min-h-[167px]">
            <div className="flex min-h-[167px] items-center justify-center rounded-2xl border border-solid border-surface-200 bg-surface-50 p-2">
              {typeof files !== "string" && (
                <Button
                  size="sm"
                  startAdornment={<Page />}
                  endAdornment={<Xmark />}
                  color={null}
                  onClick={onClear}
                  className="border border-surface-300 text-onSurface-900"
                >
                  {files[0].name}
                </Button>
              )}
            </div>
          </div>
        ) : (
          <div className="flex flex-col">
            <div
              // eslint-disable-next-line tailwindcss/no-custom-classname
              className="box__input flex min-h-[167px] w-full flex-col items-center justify-center rounded-2xl border-2 border-onSurface-300 p-2 text-center transition-[border-color] duration-150 ease-linear"
            >
              <input
                accept="application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/vnd.ms-excel,.csv"
                className="z-[1] hidden"
                id={inputId}
                type="file"
                onChange={(e) => readUploadFile(e.target.files)}
              />
              <label
                className={clsx("flex flex-col items-center gap-1 text-center")}
                htmlFor={inputId}
              >
                <Typography>Drag your file here or</Typography>
                {/* Made it a div button so it acts like normal button. */}
                <div className="relative inline-flex items-center justify-center overflow-hidden rounded-lg border border-onSurface-300 px-1.5 py-1 text-button text-onSurface-700 ring-interactive-400 transition-colors hover:bg-[linear-gradient(rgb(0_0_0/10%)_0_0)] focus:ring-2 focus:ring-offset-2 active:bg-[linear-gradient(rgb(0_0_0/20%)_0_0)] disabled:pointer-events-none disabled:cursor-not-allowed disabled:text-onSurface-400 [&_svg]:size-2">
                  Choose a file
                </div>
              </label>
              {/* {errors.map((error, i) => (
                <div className="mt-2 text-critical-500" key={i}>
                  <Typography className="text-center" variant="smallBody">
                    {error}
                  </Typography>
                </div>
              ))} */}
            </div>
          </div>
        )}
      </div>
      <div />
    </div>
  )
}

export default DragNDropCSV
